import React from 'react';
import { formatTime, calculateAge } from '../../utils/date';
import {
  XMarkIcon,
  CameraIcon,
  SparklesIcon,
  ExclamationTriangleIcon,
  BeakerIcon,
  ClockIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { ChildVM } from './useLiveSession';

function initialsOf(name: string): string {
  return (
    name
      .split(' ')
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?'
  );
}

/** Soft avatar tint per status — matches ChildBox. */
const AVATAR_TINT: Record<ChildVM['status'], string> = {
  here: 'bg-emerald-100 text-emerald-700',
  expected: 'bg-amber-100 text-amber-700',
  gone: 'bg-slate-200 text-slate-500',
  rolled: 'bg-slate-200 text-slate-500',
};

function statusLine(child: ChildVM): string {
  switch (child.status) {
    case 'here':
      return child.lastEventAt ? `Here · checked in ${formatTime(child.lastEventAt)}` : 'Here';
    case 'gone':
      return child.lastEventAt ? `Gone · checked out ${formatTime(child.lastEventAt)}` : 'Gone';
    case 'rolled':
      return 'Rolled to next class';
    case 'expected':
    default:
      return child.minutesLate > 0 ? `Expected · ${child.minutesLate}m late` : 'Expected';
  }
}

/**
 * The "click a child → everything for them" expanded surface (spec §4).
 * Renders in place of a ChildBox when that child is selected, spanning the
 * full grid width. Presentational only — this task fires callbacks; Task 7
 * wires them to real modals/mutations.
 */
const ChildTile: React.FC<{
  child: ChildVM;
  onCheckIn: () => void;
  onRelease: () => void;
  onObserve: () => void;
  onMoment: () => void;
  onIncident: () => void;
  onGiveDose: () => void;
  onHistory: () => void;
  onCollapse: () => void;
}> = ({
  child,
  onCheckIn,
  onRelease,
  onObserve,
  onMoment,
  onIncident,
  onGiveDose,
  onHistory,
  onCollapse,
}) => {
  const id = child.studentId;
  // Observations/moments/incidents need the child physically present —
  // mirrors the previous inline action row's isPresent check.
  const isPresent = child.status === 'here' || child.status === 'gone';
  const disabledTitle = isPresent ? undefined : 'Not yet checked in';
  const age = calculateAge(child.studentDateOfBirth);
  const firstDueDose = child.dueDoses[0];

  return (
    <div
      data-testid={`child-tile-${id}`}
      className="col-span-full bg-white border-2 border-primary-200 rounded-lg shadow-sm p-4 space-y-4"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            aria-hidden="true"
            className={`${
              AVATAR_TINT[child.status]
            } w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0`}
          >
            {initialsOf(child.studentName)}
          </div>
          <div className="min-w-0">
            <p className="text-base font-semibold text-slate-900 truncate">{child.studentName}</p>
            <p className="text-xs text-slate-500">{statusLine(child)}</p>
            {child.stayingOn && (
              <p
                data-testid={`staying-on-${id}`}
                className="text-xs font-medium text-violet-700 mt-0.5"
              >
                Staying on → {child.stayingOn.nextClassName}
              </p>
            )}
            {!child.stayingOn && child.status === 'here' && (
              <p data-testid={`collect-hint-${id}`} className="text-xs text-slate-400 mt-0.5">
                Collect
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onCollapse}
          data-testid={`child-tile-collapse-${id}`}
          aria-label="Collapse"
          className="text-slate-400 hover:text-slate-600 flex-shrink-0 p-1 -m-1"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        {/* Primary action + action buttons */}
        <div className="flex-1 space-y-3">
          {child.status === 'expected' && (
            <button
              type="button"
              onClick={onCheckIn}
              data-testid={`child-tile-checkin-${id}`}
              className="w-full sm:w-auto px-4 py-2.5 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"
            >
              Check in
            </button>
          )}
          {child.status === 'here' && (
            <button
              type="button"
              onClick={onRelease}
              data-testid={`child-tile-release-${id}`}
              className="w-full sm:w-auto px-4 py-2.5 rounded-md bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 inline-flex items-center gap-1.5"
            >
              Release / check out
              <ArrowRightIcon className="w-4 h-4" />
            </button>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <button
              type="button"
              onClick={onObserve}
              disabled={!isPresent}
              title={disabledTitle}
              data-testid={`child-tile-observe-${id}`}
              className="flex flex-col items-start gap-0.5 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-left hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-slate-50"
            >
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-800">
                <CameraIcon className="w-4 h-4 text-primary-600" />
                Observe
              </span>
              <span className="text-[10px] text-slate-500">learning note · can stay private</span>
            </button>

            <button
              type="button"
              onClick={onMoment}
              disabled={!isPresent}
              title={disabledTitle}
              data-testid={`child-tile-moment-${id}`}
              className="flex flex-col items-start gap-0.5 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-left hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-slate-50"
            >
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-800">
                <SparklesIcon className="w-4 h-4 text-pink-600" />
                Moment
              </span>
              <span className="text-[10px] text-slate-500">photo for parents</span>
            </button>

            <button
              type="button"
              onClick={onIncident}
              disabled={!isPresent}
              title={disabledTitle}
              data-testid={`child-tile-incident-${id}`}
              className="flex flex-col items-start gap-0.5 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-left hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-slate-50"
            >
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-800">
                <ExclamationTriangleIcon className="w-4 h-4 text-amber-600" />
                Incident
              </span>
            </button>

            {child.dueDoses.length > 0 && firstDueDose && (
              <button
                type="button"
                onClick={onGiveDose}
                data-testid={`child-tile-dose-${id}`}
                className="flex flex-col items-start gap-0.5 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-left hover:bg-rose-100"
              >
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-rose-800">
                  <BeakerIcon className="w-4 h-4 text-rose-600" />
                  Give dose
                </span>
                <span className="text-[10px] text-rose-700">
                  {child.dueDoses.length} due at {firstDueDose.slot}
                </span>
              </button>
            )}

            <button
              type="button"
              onClick={onHistory}
              data-testid={`child-tile-history-${id}`}
              className="flex flex-col items-start gap-0.5 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-left hover:bg-slate-100"
            >
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-800">
                <ClockIcon className="w-4 h-4 text-slate-600" />
                History
              </span>
            </button>
          </div>
        </div>

        {/* Side facts */}
        <div className="sm:w-44 flex-shrink-0 space-y-2 text-xs text-slate-600 bg-slate-50 rounded-md p-3">
          {age !== null && (
            <div>
              <p className="text-[10px] uppercase tracking-wide text-slate-400">Age</p>
              <p className="font-medium text-slate-800">{age} yrs</p>
            </div>
          )}
          <div>
            <p className="text-[10px] uppercase tracking-wide text-slate-400">Doses due</p>
            <p className="font-medium text-slate-800">
              {child.dueDoses.length > 0
                ? `${child.dueDoses.length} due${
                    firstDueDose ? ` · next ${firstDueDose.slot}` : ''
                  }`
                : 'None'}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wide text-slate-400">Observations today</p>
            <p className="font-medium text-slate-800">{child.observationCount}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChildTile;
